/**
 * The state machine behind the three Salesforce link forms.
 *
 * Link, re-point and clear differ in exactly three ways — which fields they
 * render, whether `reason` is required, and which token, if any, the request
 * carries. Everything else is shared: capture the failure in local state,
 * decide from the outcome table what it means, swap to the conflict panel on a
 * `409`, re-read on demand, and refuse to submit while any of that stands.
 *
 * The forms hand their differences in: `buildVariables` decides the request
 * body, `canSubmit` / `canConfirm` decide their own field rules, and
 * `useLinkMutation` names which endpoint is called.
 */
import { buildSalesforceMutationOptions } from '@/features/SalesforceAccounts/api/salesforceAccountHooks'
import type { SalesforceErrorEnvelope } from '@/features/SalesforceAccounts/lib/salesforceOutcome'
import {
  decideErrorOutcome,
  storedAccountIdOf,
} from '@/features/SalesforceAccounts/lib/salesforceOutcome'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

/** The shape both generated write hooks expose, narrowed to what is used. */
export type SalesforceLinkMutation<TVariables> = {
  mutate: (variables: TVariables) => void
  isPending: boolean
}

type MutationOptions = ReturnType<typeof buildSalesforceMutationOptions>

export type UseSalesforceLinkFormArgs<TVariables> = {
  orgId: string
  /** Every account whose reverse lookup this write can change. */
  accountIds: string[]
  toastId: string
  /** Interpolated into the outcome toast copy. Never the operator's reason. */
  toastAccountId?: string
  /** Closes the dialog. */
  onSuccess: () => void
  /**
   * Re-reads the link. Resolves `true` only when the read actually succeeded —
   * see `SalesforceLinkFormProps.onReRead`.
   */
  onReRead: () => Promise<boolean>
  /** The generated write hook for this form's endpoint. */
  useLinkMutation: (
    options: MutationOptions,
  ) => SalesforceLinkMutation<TVariables>
  /**
   * The request. `token` is present only when a compare-and-swap value is
   * being sent, and is passed through byte-for-byte.
   */
  buildVariables: (token?: string) => TVariables
  /** The form's own field rule for the footer submit. */
  canSubmit: boolean
  /**
   * The form's own field rule for the conflict panel's confirm. Defaults to
   * `canSubmit`; the first-link form narrows it, because a confirmed retry
   * sends a token and the contract requires a reason whenever one is sent.
   */
  canConfirm?: boolean
}

export type SalesforceLinkFormState = {
  /** A write or a re-read is in flight. */
  isBusy: boolean
  /** The `409` panel is showing. */
  isConflict: boolean
  /** `confirmationRequired` or `conflict` — picks the panel's heading. */
  conflictMessageKey: string
  /** The echoed token, with `null` and `''` already collapsed to `undefined`. */
  storedAccountId?: string
  /** The last re-read did not succeed, so nothing was cleared. */
  reReadFailed: boolean
  /** Copy for a `400`, shown against the field the operator can correct. */
  fieldError?: string
  /** Copy for the in-dialog error alert. */
  errorDescription?: string
  submitDisabled: boolean
  confirmDisabled: boolean
  /** Footer submit. `token` is whatever that form sends by default. */
  submit: (token?: string) => void
  /** Conflict-panel retry with the service's own echoed token. */
  confirm: (token: string) => void
  reRead: () => void
  /** Drops a stale server error the moment the operator edits a field. */
  clearFieldErrors: () => void
}

export function useSalesforceLinkForm<TVariables>({
  orgId,
  accountIds,
  toastId,
  toastAccountId,
  onSuccess,
  onReRead,
  useLinkMutation,
  buildVariables,
  canSubmit,
  canConfirm = canSubmit,
}: UseSalesforceLinkFormArgs<TVariables>): SalesforceLinkFormState {
  const { t } = useTranslation('features', { useSuspense: false })
  const queryClient = useQueryClient()

  /* Captured in `onError`, never read off `mutation.error`: calling `mutate()`
     for the retry clears that and would blank the panel mid-retry. */
  const [inlineError, setInlineError] =
    useState<SalesforceErrorEnvelope | null>(null)
  const [isUnrecognised, setIsUnrecognised] = useState(false)
  const [isReReading, setIsReReading] = useState(false)
  const [reReadFailed, setReReadFailed] = useState(false)

  const describe = (messageKey: string) =>
    String(t(`salesforceLink.outcome.${messageKey}.description`))

  /**
   * Only a *successful* re-read clears the conflict. React Query's `refetch()`
   * resolves rather than rejects when the query fails, so clearing on mere
   * resolution would silently re-arm the form with the same stale token the
   * service just refused.
   */
  const reRead = () => {
    setIsReReading(true)
    setReReadFailed(false)
    void onReRead()
      .then((succeeded) => {
        setReReadFailed(!succeeded)
        if (succeeded) {
          setInlineError(null)
        }
        // `isUnrecognised` deliberately survives a successful re-read: fresh
        // data does not tell the operator whether their last write landed.
      })
      .catch(() => setReReadFailed(true))
      .finally(() => setIsReReading(false))
  }

  const mutation = useLinkMutation(
    buildSalesforceMutationOptions({
      queryClient,
      orgId,
      accountIds,
      toastId,
      resolveCopy: (messageKey) => ({
        title: String(t(`salesforceLink.outcome.${messageKey}.title`)),
        description: String(
          t(`salesforceLink.outcome.${messageKey}.description`, {
            accountId: toastAccountId,
          }),
        ),
      }),
      onClose: onSuccess,
      onInlineError: (envelope) => {
        setReReadFailed(false)
        setInlineError(envelope)
      },
      onUnrecognised: () => {
        setIsUnrecognised(true)
        reRead()
      },
    }),
  )

  // Everything below reads the outcome table rather than an HTTP status, so
  // "this conflicts" and "retrying cannot succeed" are decided in one place.
  const decision = inlineError ? decideErrorOutcome(inlineError) : undefined
  const isConflict = decision?.kind === 'conflict'
  const isBusy = mutation.isPending || isReReading

  let errorDescription: string | undefined
  if (isUnrecognised) {
    errorDescription = describe('unknown')
  } else if (decision?.kind === 'disabled' || decision?.kind === 'failed') {
    errorDescription = describe(decision.messageKey)
  }

  const clearFieldErrors = () => {
    setIsUnrecognised(false)
    setReReadFailed(false)
    // Only errors a corrected value can resolve are dismissed by editing. A
    // conflict is a state the operator has to answer and a 503 means the
    // service is unavailable; clearing either here would silently re-enable
    // submit.
    setInlineError((current) => {
      if (!current) {
        return null
      }
      const kind = decideErrorOutcome(current).kind
      return kind === 'conflict' || kind === 'disabled' ? current : null
    })
  }

  // The footer submit is dead while the conflict panel is up: its confirm and
  // re-read are the only two answers, and re-sending from the footer would
  // either omit the echoed token or resend the stale one.
  //
  // It is equally dead after an indeterminate write (`isUnrecognised`) or a
  // failed re-read: in both cases the stored value is unknown, and submitting
  // against data that could not be refreshed is what a compare-and-swap
  // exists to prevent.
  const submitDisabled =
    !canSubmit ||
    isConflict ||
    decision?.blocksSubmit === true ||
    isUnrecognised ||
    reReadFailed ||
    isBusy
  const confirmDisabled =
    !canConfirm || decision?.blocksSubmit === true || isBusy

  const run = (token: string | undefined, blocked: boolean) => {
    if (blocked) {
      return
    }
    clearFieldErrors()
    mutation.mutate(buildVariables(token))
  }

  return {
    isBusy,
    isConflict,
    conflictMessageKey: decision?.messageKey ?? '',
    storedAccountId: isConflict
      ? storedAccountIdOf(inlineError?.result?.previousSalesforceAccountId)
      : undefined,
    reReadFailed,
    fieldError:
      decision?.kind === 'invalid' ? describe(decision.messageKey) : undefined,
    errorDescription,
    submitDisabled,
    confirmDisabled,
    submit: (token?: string) => run(token, submitDisabled),
    confirm: (token: string) => run(token, confirmDisabled),
    reRead,
    clearFieldErrors,
  }
}

/**
 * The compare-and-swap key, present only when there is a token to send. A
 * present-but-`undefined` property still serialises, and the contract refuses
 * a token supplied for an unlinked organisation — so the key has to be absent,
 * not empty.
 */
export function expectedCurrentFields(token?: string) {
  return token === undefined
    ? {}
    : { expectedCurrentSalesforceAccountId: token }
}
